import { LoadingStateModel } from '@/models/loadingState';
import CaseService from '@/services/caseService';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useStudyStore } from './study';
import { PumpPipelineFilter, type CaseModel } from '@/models/case';
import { TableColumnType, type TableColumnModel } from '@/components/common/table/interfaces/TableColumnModel';
import { useFormStore } from './forms';
import type { PaginatorData } from '@/components/common/interfaces/PaginatorData';
import { FormModel } from '@/models';


export const usePipelineStore = defineStore('pipeline', () => {

  const tableColumns = ref<TableColumnModel[]>([
    {
      id: 1,
      header: 'Case Label',
      key: 'label',
      type: TableColumnType.TEXT_WTIH_ICON,
      icon: {
        icon: 'folder',
        style: 'fas',
        color: 'rgb(133, 198, 255)'
      },
      sortable: false,
      frozen: true
    }
  ]);

  const formStore = useFormStore();
  const studyStore = useStudyStore();
  const caseList = ref<CaseModel[]>([]);
  const totalCasesCount = ref<number>(0);
  const currentPage = ref(1);
  const pageLimit = ref(10);
  const searchText = ref('');

  const isPumpStudy = computed(() => studyStore.selectedStudy?.flags?.customDataDictionary === 'PUMP');
  const pumpFilter = ref<PumpPipelineFilter | undefined>(PumpPipelineFilter.ACTIVE);

  const buildTableFormColumns = async () => {
    if (formStore.formList.length == 0) {
      await formStore.getStudyForms();
    }

    if (tableColumns.value.length === 1) {
      formStore.formList.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      formStore.formList.forEach((form, index) => {
        tableColumns.value.push({
          id: index + 2,
          header: form.title,
          key: form.identifier,
          type: TableColumnType.PIPELINE,
          sortable: false,
          identifier: form.identifier
        });
      });
    }
  };

  const casesLoader = ref(new LoadingStateModel({isLoading: false}));
  const getPipelineCases = async () => {
    casesLoader.value.startLoading();
    await buildTableFormColumns();
    try {
      let filter = pumpFilter.value;
      if (!isPumpStudy.value) {
        filter = undefined;
      }
      const caseResponse =
        await CaseService.getCases(
          studyStore.selectedStudyId!,
          currentPage.value,
          pageLimit.value,
          searchText.value,
          'pipeline',
          'createdAt',
          undefined,
          filter
        );
      caseList.value = caseResponse.cases;
      totalCasesCount.value = caseResponse.casesCount;
    } catch (error) {
      casesLoader.value.setError(error);
    } finally {
      casesLoader.value.stopLoadingAndFinish();
      await mapFormEnforcements(formStore.formList);
    }
  };

  /**
   * Reloads a single case in place. Used when an entry is saved without the pipeline unmounting (the entry dialog),
   * where a full `getPipelineCases()` would replace every row object and so drop the user's selection and scroll
   * position.
   *
   * Goes through the paginated cases endpoint in `pipeline` mode rather than `getCaseEntries`, because only that mode
   * computes the per-entry `completion` the pipeline cells render their progress bars from.
   */
  const refreshCase = async (caseIdentifier: string) => {
    const index = caseList.value.findIndex(caseItem => caseItem.identifier === caseIdentifier);
    if (index === -1) {
      // The case isn't on the current page (or the list was never loaded) — nothing to patch.
      return;
    }

    try {
      // Mirror the filter and sort the visible list was built with, so the refreshed row stays consistent with it.
      const filter = isPumpStudy.value ? pumpFilter.value : undefined;
      // A single row is all we need: case labels are unique within a study, so the label narrows the search to this
      // case and keeps the (fairly expensive) pipeline-mode entry and completion work down to one case's worth.
      const caseResponse = await CaseService.getCases(
        studyStore.selectedStudyId!,
        1,
        1,
        caseList.value[index].label,
        'pipeline',
        'createdAt',
        undefined,
        filter
      );

      // The endpoint matches labels as a substring, so confirm the row we got back really is ours before patching.
      const refreshedCase = caseResponse.cases.find(caseItem => caseItem.identifier === caseIdentifier);
      if (!refreshedCase) {
        // The case no longer matches the active filter, or another label contains this one and sorted ahead of it.
        // Leaving the row as-is beats patching it with a different case's data.
        return;
      }

      // Patch the existing row object rather than replacing it. The pipeline table has no `dataKey`, so PrimeVue
      // matches selected rows by deep equality — swapping in a new object with updated entries would no longer match
      // the stale object held in the selection and the row would silently lose its checkbox.
      Object.assign(caseList.value[index], refreshedCase);
      await mapFormEnforcements(formStore.formList);
    } catch (error) {
      casesLoader.value.setError(error);
    }
  };

  const handleCaseSearch = (searchQuery: string) => {
    currentPage.value = 1;
    searchText.value = searchQuery;
    getPipelineCases();
  };

  const mapFormEnforcements = async (forms: FormModel[]) => {
    for (const caseItem of caseList.value) {
      for (let i = 0; i < forms.length; i++) {
        const form = forms[i];
        if (caseItem.enforcements && caseItem.enforcements[form.identifier]?.enforceOrder) {
          if (!caseItem.entries || (caseItem.entries && caseItem.entries[form.identifier].length == 0)) {
            forms.forEach((formItem, index) => {
              if (index > i && formItem.identifier !== form.identifier && caseItem.enforcements) {
                caseItem.enforcements[formItem.identifier].enforceOrder = true;
              }
            });
          }
          break;
        }
      }
    }
  };

  const onPageChange = (pageData: PaginatorData) => {
    currentPage.value = pageData.page + 1;
    pageLimit.value = pageData.rows;
    getPipelineCases();
  };

  const resetStore = () => {
    tableColumns.value.splice(1, tableColumns.value.length - 1);
    currentPage.value = 1;
  };

  return {
    getPipelineCases,
    refreshCase,
    onPageChange,
    handleCaseSearch,
    resetStore,
    tableColumns,
    casesLoader,
    caseList,
    totalCasesCount,
    currentPage,
    pageLimit,
    pumpFilter,
    isPumpStudy
  };
});
